import React from 'react';
import { useNavigate } from 'react-router-dom';

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  
  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      <button 
        onClick={() => navigate('/')} 
        className="flex items-center text-gray-600 mb-6 hover:text-gray-900"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
        </svg>
        Back
      </button>
      
      <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>
      <p className="text-sm text-gray-600 mb-6">Effective Date: April 11, 2025</p>
      
      <div className="prose max-w-none">
        <p>
          Welcome to Aptmetrix! Your privacy is important to us. This Privacy Policy explains how we collect, use, and protect your information.
        </p>
        
        <p>
          This Privacy Policy is designed to comply with the General Data Protection Regulation (GDPR), South Korea's Personal Information Protection Act (PIPA), and other applicable data protection laws.
        </p>
        
        <h2 className="text-xl font-semibold mt-6 mb-3">1. Introduction</h2>
        <p>
          Aptmetrix (https://aptmetrix.kr) is a real estate data analytics platform, owned and operated by Insight&Myung, LLC ("we", "our", or "us"). This Privacy Policy explains how we collect, use, and protect your information in accordance with applicable data protection laws.
        </p>
        
        <h2 className="text-xl font-semibold mt-6 mb-3">2. Information We Collect</h2>
        <p>
          We collect the following personal data from our users:
        </p>
        <ul className="list-disc pl-6 mb-4">
          <li>Name</li>
          <li>Email</li>
          <li>Payment information</li>
        </ul>
        <p>
          Additionally, through third-party APIs, we may collect more user data as required to provide and enhance our services.
          We also collect non-personal data such as web cookies to enhance your experience.
        </p>
        
        <p>
          <strong>Legal Basis for Processing (GDPR Article 6):</strong>
        </p>
        <ul className="list-disc pl-6 mb-4">
          <li>Consent: We process your data based on your explicit consent.</li>
          <li>Contract: Processing is necessary for the performance of our contract with you.</li>
          <li>Legitimate Interests: We process data for our legitimate business interests.</li>
        </ul>
        
        <h2 className="text-xl font-semibold mt-6 mb-3">3. Data Usage</h2>
        <p>
          The information we collect is used for the following purposes:
        </p>
        <ul className="list-disc pl-6 mb-4">
          <li>Order processing</li>
          <li>Providing performant service for users</li>
          <li>Service improvement</li>
        </ul>
        
        <h2 className="text-xl font-semibold mt-6 mb-3">4. Data Sharing</h2>
        <p>
          We do not share your data with any other parties except as required by law or with your explicit consent.
        </p>
        
        <h2 className="text-xl font-semibold mt-6 mb-3">5. International Data Transfers</h2>
        <p>
          We may transfer, process, and store your personal information on servers located outside of your home country, including in countries that may not provide the same level of data protection. We ensure that any such transfers comply with applicable data protection laws, including GDPR and PIPA. By using our services, you consent to the transfer of your information to these countries.
        </p>
        
        <h2 className="text-xl font-semibold mt-6 mb-3">6. Data Retention</h2>
        <p>
          We retain your personal data only for as long as necessary to fulfill the purposes for which it was collected, in accordance with legal requirements.
        </p>
        <p>
          For users in South Korea, we retain your personal information for the following periods:
        </p>
        <ul className="list-disc pl-6 mb-4">
          <li>Account information: Until account deletion or 1 year after last login</li>
          <li>Payment information: 5 years (as required by Korean commercial law)</li>
          <li>Usage logs: 3 months</li>
        </ul>
        <p>
          After these periods, your information will be securely deleted or anonymized unless we are required by law to retain it longer.
        </p>
        
        <h2 className="text-xl font-semibold mt-6 mb-3">7. Your Rights</h2>
        <p>
          Under GDPR, PIPA, and other applicable data protection laws, you have the following rights:
        </p>
        <ul className="list-disc pl-6 mb-4">
          <li>Right to access your personal data</li>
          <li>Right to rectification of inaccurate data</li>
          <li>Right to erasure ('right to be forgotten')</li>
          <li>Right to restrict processing</li>
          <li>Right to data portability</li>
          <li>Right to object to processing</li>
          <li>Rights related to automated decision-making and profiling</li>
        </ul>
        
        <p>
          Under PIPA, South Korean users also have the following rights:
        </p>
        <ul className="list-disc pl-6 mb-4">
          <li>Right to be informed about the collection and use of personal information</li>
          <li>Right to consent to or refuse the collection, use, and provision of personal information</li>
          <li>Right to request access to and correction of errors in their personal information</li>
          <li>Right to request suspension of processing of their personal information</li>
          <li>Right to request deletion of their personal information</li>
        </ul>
        
        <p>
          To exercise these rights, please contact us at jinhom6225@insightmyung.com.
        </p>
        
        <h2 className="text-xl font-semibold mt-6 mb-3">8. Data Security</h2>
        <p>
          We implement appropriate technical and organizational measures to ensure a level of security appropriate to the risk, including encryption and access controls.
        </p>
        
        <h2 className="text-xl font-semibold mt-6 mb-3">9. Cookies and Tracking</h2>
        <p>
          We use cookies and similar tracking technologies. You can manage your cookie preferences through your browser settings.
        </p>
        
        <h2 className="text-xl font-semibold mt-6 mb-3">10. Children's Privacy</h2>
        <p>
          We do not knowingly collect any data from children under 16 years of age. If you are under 16, please do not use our services or provide any personal information.
        </p>
        
        <h2 className="text-xl font-semibold mt-6 mb-3">11. Supervisory Authority</h2>
        <p>
          You have the right to lodge a complaint with a supervisory authority.
        </p>
        <ul className="list-disc pl-6 mb-4">
          <li>For users in the European Union, you can find contact details for your national data protection authority on the European Commission website: <a href="https://ec.europa.eu/justice/article-29/structure/data-protection-authorities/index_en.htm" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">European Commission</a></li>
          <li>For users in South Korea, you can file complaints with the Personal Information Protection Commission (PIPC): <a href="https://www.pipc.go.kr/" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">PIPC</a></li>
        </ul>
        
        <h2 className="text-xl font-semibold mt-6 mb-3">12. Contact Information</h2>
        <p>
          If you have any questions or concerns about this Privacy Policy, please contact us at:
        </p>
        <p>
          Insight&Myung, LLC<br />
          Email: jinhom6225@insightmyung.com
        </p>
        
        <p className="mt-6">
          Thank you for using Aptmetrix!
        </p>
      </div>
    </div>
  );
} 